import { Report as PbiReport, models as PbiModels } from "powerbi-client"
import {US_Filter} from "../../../api/models/us_filter";
import {DeepCopy} from "../../../api/models-ex/deep-copy";
import {
  EditInteractiveFilterDialogComponent,
  EditInteractiveFilterDialogData
} from "../../../dialogs/edit-interactive-filter-dialog/edit-interactive-filter-dialog.component";
import {UserStateEx} from "../../../api/models-ex/user-state-ex";
import {DialogResultType} from "../../../dialogs/DialogResult";
import {MessageDialogComponent} from "../../../dialogs/message-dialog/message-dialog.component";
import {ConfirmDialogComponent} from "../../../dialogs/confirm-dialog/confirm-dialog.component";
import {InteractivesService} from "../../../api/services/interactives.service";
import {US_Interactive} from "../../../api/models/us_interactive";
import {US_ModifiedInteractive} from "../../../api/models/us_modified-interactive";
import {UserState} from "../../../api/models/user-state";
import {MatDialog} from "@angular/material/dialog";
import {GlobalApp} from "../../../tools/global";
import {SubscriptionService} from "../../../api/services/subscription.service";

export class FilterService {

  public interactFilters: US_Filter[] = [];

  constructor(
    private userId: number | null,
    private userState: UserState,
    private dialog: MatDialog,
    private app: GlobalApp,
    private loadUserState: {(): Promise<UserState>},
    private subscriptionService: SubscriptionService,
    private interactiveId: string | undefined,
    private modifiedInteractiveId: string | undefined,
    private currentInteractive: US_Interactive,
    private currentModifiedInteractive: US_ModifiedInteractive,
    private embeddedReport: PbiReport,
    private interactiveService: InteractivesService,
    public selectedFilter: US_Filter = null,
    public appliedFilter: US_Filter = null
  ) {
  }

  refreshState(): Promise<void> {
    return new Promise<void>((resolve, error) => {
      this.loadUserState().then(result => {
        this.userState = result;
        if (this.currentInteractive) {
          this.currentInteractive = this.userState.interactives.find(x => x.interactId === this.currentInteractive.interactId);
        } else if (this.currentModifiedInteractive) {
          this.currentModifiedInteractive = this.userState.modifiedInteractives.find(x => x.modInteractId === this.currentModifiedInteractive.modInteractId);
        }

        this.loadFilters();
        resolve();
      })
    });
  }

  applyIfFilter(filterId: string) {
    if (filterId !== null && filterId !== '-1') {
      for (let i = 0; i < this.interactFilters.length; i++) {
        if (this.interactFilters[i].filterId == parseInt(filterId)) {
          this.selectedFilter = this.interactFilters[i];
          this.applyFilter();
          break;
        }
      }
    }
  }

  loadFilters(): void {
      if (this.currentInteractive) {
        this.interactFilters = this.currentInteractive.filters;
      } else if (this.currentModifiedInteractive) {
        this.interactFilters = this.currentModifiedInteractive.filters;
      }
  }

  getReportFilters(): Promise<string> {
    return new Promise<string>((resolve, _) => {
      this.embeddedReport.getFilters().then((filters) => {
        if (filters.length > 0) {
          resolve(JSON.stringify(filters));
        } else {
          resolve("");
        }
      });
    });
  }

  getPageFilters(): Promise<string> {
    return new Promise<string>((resolve, _) => {
      this.embeddedReport.getPages().then((allPages) => {
        let pageFilterPromises: any[] = [];
        allPages.forEach((page) => {
          pageFilterPromises.push(page.getFilters());
        });

        Promise.all(pageFilterPromises).then((results) => {
          if (results.length > 0) {
            let pageFilters: string[] = [];

            for (let i = 0; i < results.length; i++) {
              pageFilters.push(JSON.stringify(results[i]));
            }

            resolve(JSON.stringify(pageFilters));
          } else {
            resolve("");
          }
        });
      });
    });
  }

  loadSelectedFilter(filter: US_Filter): boolean {
    this.selectedFilter = filter;
    this.applyFilter();
    return false;
  }

  applyFilter(): boolean {
    if (this.selectedFilter !== null) {
      this.appliedFilter = this.selectedFilter;
      this.injectFilters(
        this.appliedFilter.reportFiltersJson,
        this.appliedFilter.pageFiltersJson
      );

      this.interactiveService.interactivesLogAppliedFilter({
        interactiveId: this.interactiveId ? parseInt(this.interactiveId) : undefined,
        modifiedInteractiveId: this.modifiedInteractiveId ? parseInt(this.modifiedInteractiveId) : undefined,
        filterId: this.selectedFilter.filterId,
      }).subscribe(_ => {})

    } else {
      this.appliedFilter = null;
      this.selectedFilter = null;
      this.embeddedReport.reload();
    }

    return false;
  }

  injectFilters(mainFilterJSON: string, pageFiltersJSON: string) {

    if(mainFilterJSON && mainFilterJSON !== '') {
      const reportFilters: PbiModels.IFilter[] = JSON.parse(mainFilterJSON);
      this.embeddedReport.setFilters(reportFilters);
    }

    const pageFilters: string[] = JSON.parse(pageFiltersJSON);

    this.embeddedReport.getPages().then((allPages) => {
      for (let page = 0; page < allPages.length; page++) {
        if (page < pageFilters.length) {
          const filters: PbiModels.IFilter[] = JSON.parse(pageFilters[page]);
          if (filters.length > 0) {
            allPages[page].setFilters(filters);
          }
        }
      }
    });
  }

  editSelectedFilter(filter: US_Filter): boolean {
    this.selectedFilter = filter;
    //this.applyFilter();
    this.editFilter();

    return false;
  }

  editFilter(): boolean {
    if (this.selectedFilter !== null) {
      this.getReportFilters().then((_) => {
        this.getPageFilters().then((_) => {
          if (this.selectedFilter.canEdit) {
            let filter = DeepCopy.copy(this.selectedFilter);

            const dialogData: EditInteractiveFilterDialogData = {
              userSubscriptions: this.currentInteractive
                ? this.currentInteractive.subscriptionHeaders
                : this.currentModifiedInteractive.subscriptionHeaders,
              filter,
              shareState: UserStateEx.getFilterShareState(
                this.userState,
                filter
              ),
              isNew: false,
              isOwner: this.selectedFilter.owner,
              updateData: false,
            };

            const dialogRef = this.dialog.open(
              EditInteractiveFilterDialogComponent,
              {
                width: "500px",
                data: dialogData,
              }
            );

            dialogRef.afterClosed().subscribe((result) => {
              if (
                result !== null &&
                result.ResultType !== null &&
                result.ResultType === DialogResultType.Ok
              ) {
                const data = result.ResultData as EditInteractiveFilterDialogData;

                this.getReportFilters().then((reportFilters) => {
                  this.getPageFilters().then((pageFilters) => {
                    if (data.updateData) {
                      data.filter.pageFiltersJson = pageFilters;
                      data.filter.reportFiltersJson = reportFilters;
                    }

                    this.app.pushLoading("Saving Filter");
                    this.interactiveService
                      .interactivesSaveInteractiveFilter({ filter: data.filter, userId: this.userId})
                      .subscribe(
                        (result) => {
                          //0 = Not Authorized
                          //-1 = Invalid User
                          // > 0 = FilterId

                          if (result === 0) {
                            MessageDialogComponent.showDialog(
                              this.dialog,
                              "Error",
                              "Not Authorized To Modify Filter"
                            );
                            this.app.popLoading();
                          } else if (result === -1) {
                            MessageDialogComponent.showDialog(
                              this.dialog,
                              "Error",
                              "Invalid User"
                            );
                            this.app.popLoading();
                          } else if (result > 0) {
                            this.interactiveService
                              .interactivesUpdateFilterSharing({
                                filterId: result,
                                updateSharingStatus: UserStateEx.getSharingStatus(
                                  data.shareState
                                ),
                              })
                              .subscribe((_) => {
                                this.refreshState().then(() => {
                                  for (
                                    let i = 0;
                                    i < this.interactFilters.length;
                                    i++
                                  ) {
                                    if (
                                      this.interactFilters[i].filterId ===
                                      result
                                    ) {
                                      this.selectedFilter = this.interactFilters[
                                        i
                                        ];
                                      this.appliedFilter = this.interactFilters[
                                        i
                                        ];
                                    }
                                  }
                                  this.applyFilter();
                                  this.app.popLoading();
                                });
                              });
                          }
                        },
                        (_) => {}
                      );
                  });
                });
              }
            });
          }
        });
      });
    }

    return false;
  }

  deleteSelectedFilter(filter: US_Filter): boolean {
    ConfirmDialogComponent.showDialog(
      this.dialog,
      "Confirm Delete",
      "Are you sure you want to delete this filter?"
    ).subscribe((result) => {
      if (result.ResultType == DialogResultType.Ok) {
        this.selectedFilter = filter;
        this.deleteFilter();
      }
    });

    return false;
  }

  deleteFilter(): boolean {
    if (this.selectedFilter !== null) {
      this.app.pushLoading("Deleting Filter");
      this.interactiveService
        .interactivesDeleteInteractiveFilter({filterId: this.selectedFilter.filterId, userId: this.userId})
        .subscribe(
          (result) => {
            if (result) {
              this.refreshState().then(() => {
                this.app.popLoading();
              });
            }
          },
          (_) => {}
        );
    }

    return false;
  }

  createFilter(): boolean {
    this.getReportFilters().then((reportFilters) => {
      this.getPageFilters().then((pageFilters) => {
        const filter: US_Filter = {
          filterId: -1,
          includeInEmbedded: false,
          interactId: this.interactiveId ? parseInt(this.interactiveId) : null,
          modifiedInteractId: this.currentModifiedInteractive ? this.currentModifiedInteractive.modInteractId : null,
          subscriptionId: this.currentInteractive
            ? this.currentInteractive.subscriptionHeaders[0].subscriptionId
            : this.currentModifiedInteractive.subscriptionHeaders[0].subscriptionId,
          userId: -1,
          name: "<New Filter>",
          reportFiltersJson: reportFilters,
          pageFiltersJson: pageFilters,
          subscriptionName: "",
          organizationName: "",
          createdBy: "",
          owner: true,
          shares: [],
          canEdit: true,
          deleted: false,
        };

        const dialogData: EditInteractiveFilterDialogData = {
          userSubscriptions: this.currentInteractive ? this.currentInteractive.subscriptionHeaders : this.currentModifiedInteractive.subscriptionHeaders,
          filter,
          shareState: UserStateEx.getFilterShareState(this.userState, filter),
          isNew: true,
          isOwner: true,
          updateData: false,
        };

        const dialogRef = this.dialog.open(
          EditInteractiveFilterDialogComponent,
          {
            width: "500px",
            data: dialogData,
          }
        );

        dialogRef.afterClosed().subscribe((result) => {
          if (
            result !== null &&
            result.ResultType !== null &&
            result.ResultType === DialogResultType.Ok
          ) {
            const data = result.ResultData as EditInteractiveFilterDialogData;

            this.app.pushLoading("Creating Filter");
            this.interactiveService
              .interactivesSaveInteractiveFilter({filter: data.filter, userId: this.userId})
              .subscribe(
                (result) => {
                  //0 = Not Authorized
                  //-1 = Invalid User
                  // > 0 = FilterId

                  if (result === 0) {
                    MessageDialogComponent.showDialog(
                      this.dialog,
                      "Error",
                      "Not Authorized To Modify Filter"
                    );
                    this.app.popLoading();
                  } else if (result === -1) {
                    MessageDialogComponent.showDialog(
                      this.dialog,
                      "Error",
                      "Invalid User"
                    );
                    this.app.popLoading();
                  } else if (result > 0) {
                    this.interactiveService
                      .interactivesUpdateFilterSharing({
                        filterId: result,
                        updateSharingStatus: UserStateEx.getSharingStatus(
                          data.shareState
                        ),
                      })
                      .subscribe((_) => {
                        this.refreshState().then(() => {
                          for (
                            let i = 0;
                            i < this.interactFilters.length;
                            i++
                          ) {
                            if (this.interactFilters[i].filterId === result) {
                              this.selectedFilter = this.interactFilters[i];
                            }
                          }
                          this.applyFilter();
                          this.app.popLoading();
                        });
                      });
                  }
                },
                (_) => {}
              );
          }
        });
      });
    });

    return false;
  }
}
